import * as Sentry from "@sentry/react";

const { logger } = Sentry;

// Custom span tracking for newsletter features
export const sentryUtils = {
  // Newsletter form interactions
  trackNewsletterSubmission: (email: string, step: 'email' | 'interests') => {
    return Sentry.startSpan(
      {
        op: "ui.action",
        name: `Newsletter ${step} Submission`,
      },
      (span) => {
        span.setAttribute("user.email", email);
        span.setAttribute("form.step", step);
        span.setAttribute("conversion.funnel", "newsletter_signup");
        
        logger.info(logger.fmt`Newsletter form submission: ${step}`, {
          email,
          step,
          timestamp: new Date().toISOString(),
        });
      }
    );
  },

  // Template interactions
  trackTemplateInteraction: (action: string, templateId?: string) => {
    return Sentry.startSpan(
      {
        op: "ui.interaction",
        name: `Template ${action}`,
      },
      (span) => {
        if (templateId) {
          span.setAttribute("template.id", templateId);
        }
        span.setAttribute("interaction.type", action);
        
        logger.debug(logger.fmt`Template interaction: ${action}`, {
          action,
          templateId,
          timestamp: new Date().toISOString(),
        });
      }
    );
  },

  // Supabase function calls
  trackSupabaseFunction: async <T>(
    functionName: string,
    operation: () => Promise<T>,
    context?: Record<string, any>
  ): Promise<T> => {
    return Sentry.startSpan(
      {
        op: "http.client",
        name: `Supabase Function: ${functionName}`,
      },
      async (span) => {
        span.setAttribute("supabase.function", functionName);
        if (context) {
          Object.entries(context).forEach(([key, value]) => {
            span.setAttribute(`context.${key}`, String(value));
          });
        }

        try {
          logger.debug(logger.fmt`Calling Supabase function: ${functionName}`, {
            function: functionName,
            context,
          });

          const result = await operation();
          
          span.setStatus({ code: 1 }); // OK
          logger.info(logger.fmt`Supabase function success: ${functionName}`, {
            function: functionName,
            success: true,
          });
          
          return result;
        } catch (error) {
          span.setStatus({ code: 2, message: String(error) }); // ERROR
          
          logger.error(logger.fmt`Supabase function error: ${functionName}`, {
            function: functionName,
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
          });
          
          Sentry.captureException(error, {
            tags: {
              component: 'supabase',
              function: functionName,
            },
            extra: context,
          });
          
          throw error;
        }
      }
    );
  },

  // Set user context when available
  setUserContext: (email: string, additionalData?: Record<string, any>) => {
    Sentry.setUser({
      email,
      ...additionalData,
    });
    
    logger.info("User context set", {
      email,
      ...additionalData,
    });
  },

  // Log conversion funnel steps
  trackConversionFunnel: (step: string, stepNumber: number, additionalData?: Record<string, any>) => {
    return Sentry.startSpan(
      {
        op: "conversion.funnel",
        name: `Funnel Step: ${step}`,
      },
      (span) => {
        span.setAttribute("funnel.step", step);
        span.setAttribute("funnel.step_number", stepNumber);
        
        if (additionalData) {
          Object.entries(additionalData).forEach(([key, value]) => {
            span.setAttribute(`funnel.${key}`, String(value));
          });
        }
        
        logger.info(logger.fmt`Conversion funnel step: ${step}`, {
          step,
          stepNumber,
          ...additionalData,
        });
      }
    );
  },

  // Log form engagement
  trackFormEngagement: (formType: string, engagement: string, data?: Record<string, any>) => {
    logger.info(logger.fmt`Form engagement: ${formType} - ${engagement}`, {
      formType,
      engagement,
      timestamp: new Date().toISOString(),
      ...data,
    });
  },

  // Manual error capture with context
  captureError: (error: Error, context?: Record<string, any>) => {
    Sentry.captureException(error, {
      extra: context,
    });
    
    logger.error("Manual error capture", {
      error: error.message,
      stack: error.stack,
      ...context,
    });
  },

  // Performance monitoring for critical sections
  measurePerformance: <T>(name: string, operation: () => T | Promise<T>): Promise<T> => {
    return Sentry.startSpan(
      {
        op: "measure",
        name: `Performance: ${name}`,
      },
      async (span) => {
        const startTime = performance.now();
        
        try {
          const result = await operation();
          const duration = performance.now() - startTime;
          
          span.setAttribute("performance.duration_ms", duration);
          logger.debug(logger.fmt`Performance measurement: ${name}`, {
            operation: name,
            duration_ms: duration,
          });
          
          return result;
        } catch (error) {
          const duration = performance.now() - startTime;
          span.setAttribute("performance.duration_ms", duration);
          span.setStatus({ code: 2, message: String(error) });
          
          logger.warn(logger.fmt`Performance measurement failed: ${name}`, {
            operation: name,
            duration_ms: duration,
            error: error instanceof Error ? error.message : String(error),
          });
          
          throw error;
        }
      }
    );
  },
};

export { logger };
export default sentryUtils;